import { useMemo } from 'react';
import {
    DepositTaxRateMode,
    RateGroupOptions,
    SettlementExtent,
    SettlementRuleAction,
    SettlementRuleTrigger,
    SettlementRuleType,
    SettlementValueType,
    TaxEnvironment,
} from '@mews-commander/data-access';
import { CountryCode } from '@mews-commander/enums';
import { getTaxEnvironment } from '@mews-commander/legacy-entities/src/ducks/taxEnvironments';
import {
    getCurrentEnterpriseTaxEnvironmentCode,
    getCurrentEnterpriseDefaultCurrencyCode,
    getCurrentEnterpriseCountryCode,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { useCommanderSelector } from '@mews-commander/utils';
import { RateGroupCreateFormModel } from '../RateGroupForm';

export const useInitialValues = (serviceId: string) => {
    const defaultCurrencyCode = useCommanderSelector<string>(getCurrentEnterpriseDefaultCurrencyCode);
    const enterpriseCountryCode = useCommanderSelector<string>(getCurrentEnterpriseCountryCode);
    const taxEnvironmentCode = useCommanderSelector<string>(getCurrentEnterpriseTaxEnvironmentCode);
    const { defaultSettlementDepositTaxRateMode } = useCommanderSelector<TaxEnvironment>((state) =>
        getTaxEnvironment(state, { taxEnvironmentCode })
    );

    const hasTaxedDeposits = ![DepositTaxRateMode.None, DepositTaxRateMode.Zero].includes(
        defaultSettlementDepositTaxRateMode
    );
    const isUsEnvironment = enterpriseCountryCode === CountryCode.UnitedStates;
    const useAutomaticDepositSettlement = hasTaxedDeposits && !isUsEnvironment;

    return useMemo(
        () =>
            ({
                serviceId,
                names: {},
                shortNames: {},
                descriptions: {},
                settlementExtent: [
                    SettlementExtent.AdditionalExpenses,
                    SettlementExtent.TimeUnits,
                    SettlementExtent.Products,
                ],
                options: [
                    useAutomaticDepositSettlement
                        ? RateGroupOptions.AutomaticDepositSettlement
                        : RateGroupOptions.CreateSettlementTask,
                ],
                settlementCurrencyCode: defaultCurrencyCode,
                settlementRule: {
                    action: SettlementRuleAction.ChargeCreditCard,
                    type: SettlementRuleType.Automatic,
                    trigger: SettlementRuleTrigger.Start,
                    offset: 'PT0S',
                    value: {
                        discriminator: SettlementValueType.Percentage,
                        value: { value: 1 },
                    },
                },
            }) satisfies RateGroupCreateFormModel,
        [serviceId, defaultCurrencyCode, useAutomaticDepositSettlement]
    );
};
